import { Entity } from '@api/api/entity-schema';
import {
  CreateChartOptionsWithConfig,
  PriceHoverData,
  RangeHorizon,
  ResampleIntervals,
} from '@chart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { getChartV2PaneId } from '@helpers/utils/chart-v2-utils/chart-v2-utils';
import {
  ChartAreaWrapper,
  ChartAreaWrapperProps,
} from '@terminal/components/chart-area-wrapper/ChartAreaWrapper';
import { ChartAreaV2 } from '@terminal/modules/simple-chart/components/chart-area-v2/ChartAreaV2';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useChartV2Insights } from '@terminal/views/chart-v2/hooks/useChartV2Insights';
import {
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ValidChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MiniLegend } from './components/mini-legend/MiniLegend';
import styles from './MultiLineChartModule.module.scss';

export interface TooltipSortConfig {
  field: keyof PriceHoverData;
  desc: boolean;
}
export interface MultiLineChartModuleProps {
  entities: Pick<Entity, 'tag' | 'default_snake' | 'ticker' | 'exchange'>[];
  chartConfig?: ChartAreaWrapperProps['chartConfig'];
  tooltipSortConfig?: TooltipSortConfig;
  source: PosthogTrackingSource;
}

const DEFAULT_SIMPLE_CHART_CONFIG: Partial<
  CreateChartOptionsWithConfig['config']
> = {
  enableZoom: true,
  enableHover: true,
  hoverVariant: 'simple',
};

export const MultiLineChartModule = ({
  entities,
  chartConfig,
  tooltipSortConfig,
  source,
}: MultiLineChartModuleProps) => {
  useTranslation(['chart', 'analyze', 'countries']);

  const initialChartSettings: ValidChartSettings = {
    assets: entities.map(entity => ({
      tag: entity.tag,
      snake: entity.default_snake,
      paneIndex: 0,
    })),
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.OneYear,
    domain: null,
    hidden: undefined,
    paneSettings: {
      0: {
        yAxisType: 'merged',
      },
    },
  };

  const { colors } = useChartTheme(['soft', 'multiline']);

  const {
    assetWithError,
    chartData,
    hasError,
    hasEmpty,
    dailyTimeseries,
    chartState,
    retryInitialChartLoad,
    changeRange,
    changeSeriesType,
    primaryAsset,
    primaryPane,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: !initialChartSettings,
  });

  const { horizon, resample, chartPanes } = chartState;

  const finalChartConfig = {
    ...DEFAULT_SIMPLE_CHART_CONFIG,
    ...chartConfig,
  };

  const chartV2Series = useMemo(
    () =>
      terminalPanesToSeries(chartPanes, {
        shouldUseLineColorToken: true,
        showLatestPrice: false,
      }).map(series => ({ ...series, yAxisLabel: '' })),
    [chartPanes]
  );

  const chartV2DefaultPriceDisplayByPane = useMemo(
    () => ({
      ...terminalPanesToDefaultPriceDisplayByPane(chartPanes),
      [getChartV2PaneId(0)]: 'indexed' as const,
    }),
    [chartPanes]
  );

  const chartV2Insights = useChartV2Insights({
    chartPanes,
    eventsConfig: { TMI: false },
    small: true,
    source,
  });

  const isLoading = !hasError && !hasEmpty && !dailyTimeseries && !chartData[0];

  if (isLoading) {
    return (
      <div className={styles.skeletonWrapper} data-testid="chart-loading">
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '350px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      </div>
    );
  }

  if (!primaryAsset || !primaryPane) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="multi-line-chart">
      <section className={styles.body}>
        <ChartAreaWrapper
          className={styles.chartAreaWrapper}
          assetWithError={assetWithError}
          hasError={hasError}
          hasEmpty={hasEmpty}
          colors={colors}
          chartData={chartData}
          onRetry={retryInitialChartLoad}
          renderChartArea={() => (
            <ChartAreaV2
              series={chartV2Series}
              chartData={chartData}
              primaryPane={primaryPane}
              horizon={horizon}
              resample={resample}
              availableHorizons={[]}
              disable1DHorizon={false}
              insights={chartV2Insights}
              enableZoom={!!finalChartConfig.enableZoom}
              enableHover={!!finalChartConfig.enableHover}
              onHorizonChange={changeRange}
              changeSeriesType={changeSeriesType}
              defaultPriceDisplayByPane={chartV2DefaultPriceDisplayByPane}
              tooltipSortConfig={tooltipSortConfig}
              showPriceDisplayToggle={false}
            />
          )}
        />
        <MiniLegend
          labels={chartData.map(c => c.entity.formattedTicker)}
          colors={colors.getMulti()}
        />
      </section>
    </div>
  );
};
